import { Box, Button, Grid2, Typography } from '@mui/material'
import { fetchThreadHistory, initiateNewThread, resetNewChatId, selectIfAssistanceResponseLoading, selectIfThreadHistoryLoaded, selectNewThreadId, selectThreadFromHistoryById, selectThreadHistory, selectThreadHistoryLoadingError, sendQueryToThread } from '../store/slices/threadsSlice';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import AIRepsonseLoadingProgress from '../components/shared/AIRepsonseLoadingProgress';
import { APP_ROUTES } from '../constants/AppRoutes';
import AddIcon from '@mui/icons-material/Add';
import { AppDispatch } from '../store/store';
import AssistantResponse from '../components/threads/AssistantResponse';
import BaloonShaped from '../components/styled/BaloonShaped';
import DarkAdnocLoadingProgress from '../components/shared/DarkAdnocLoadingProgress';
import DataSources from '../components/shared/DataSources';
import { EXAMPLE_PROMP_SUGGESTIONS } from '../constants/SuggestedPrompts';
import ErrorState from '../components/shared/ErrorState';
import PromptInput from '../components/shared/PromptInput'
import SavedForLater from '../components/shared/SavedForLater';
import ThreadsHistory from '../components/shared/ThreadsHistory';
import UserQuery from '../components/threads/UserQuery';
import { selectAppliedDataSourcesAsRequest } from '../store/slices/dataSourcesSlice';
import { selectBookmarksList } from '../store/slices/bookmarksSlice';

const NEW_THREAD_ID: string = "new";

function AskGuardian() {
  const { threadId } = useParams();
  const [searchParams] = useSearchParams();
  const entry_id = searchParams.get("entry_id");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const loadingResponse = useSelector(selectIfAssistanceResponseLoading);
  const errorLoadingReponse = useSelector(selectThreadHistoryLoadingError);
  const newThreadId = useSelector(selectNewThreadId)

  const appliedDataSources = useSelector(selectAppliedDataSourcesAsRequest, shallowEqual);

  const threadsHistoryLoaded = useSelector(selectIfThreadHistoryLoaded);
  const thread = useSelector(selectThreadFromHistoryById(threadId ?? NEW_THREAD_ID));
  const threadHistory = useSelector(selectThreadHistory);

  const bookmarks = useSelector(selectBookmarksList);
  const [isTextStreaming, setIsTextStreaming] = useState(false);

  const onPromptSubmit = (query: string) => {
    const payload = {
      threadId: threadId ?? NEW_THREAD_ID,
      request: {
        content: {
          text: query,
          sources: appliedDataSources
        },
        role: "user",
      }
    };

    if (!threadId || threadId === NEW_THREAD_ID) {
      dispatch(initiateNewThread(payload))
    } else {
      dispatch(sendQueryToThread(payload))
    }
  }

  useEffect(() => {
    if (threadsHistoryLoaded === null) {
      dispatch(fetchThreadHistory())
    }
  }, [dispatch, threadsHistoryLoaded])

  useEffect(() => {
    if (newThreadId !== NEW_THREAD_ID && entry_id === null) {
      navigate(`${APP_ROUTES.ASK_GUARDIAN}/${newThreadId}`, { replace: true })
      dispatch(resetNewChatId())
    }
  }, [dispatch, entry_id, navigate, newThreadId])

  const handleAddNewChat = () => {
    navigate(APP_ROUTES.ASK_GUARDIAN, { replace: true })
  }

  const threadContainerRef = useRef<HTMLDivElement>(null);

  const handleOnTextStreaming = (value: boolean) => {
    setIsTextStreaming(value);
    autoScroll();
  }

  const autoScroll = () => {
    if (threadContainerRef) {
      threadContainerRef.current?.lastElementChild?.scrollIntoView();
    }
  }

  useEffect(() => {
    if (threadContainerRef && loadingResponse) {
      threadContainerRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loadingResponse, thread])

  useEffect(() => {
    if (entry_id) {
      const element = document.getElementById(`e-${entry_id!.replace('-', '')}`)
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entry_id])

  return (
    <>
      <Grid2
        container
        size={{ xs: 12, lg: 10 }}
        sx={{
          width: '100vw',
          height: '100vh',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'row',
        }}>

        <Grid2 size={{ xs: 4.5 }} sx={{
          position: 'relative',
          zIndex: 12,
          paddingTop: 12,
          boxSizing: 'border-box',
          display: "flex",
          flexDirection: 'column',
          gap: 2,
        }}>
          <Box sx={{
            width: '100%',
            height: 'max-content',
            display: 'grid',
            alignItems: 'center',
            gridTemplateColumns: 'auto .6fr',
            background: 'linear-gradient(90deg, rgba(255, 255, 255, 0) -7.34%, rgba(255, 255, 255, 0.52) 50%)',
            paddingX: 2.5,
            paddingY: 2,
            boxSizing: 'border-box',
          }}>
            <Typography
              variant='h4'
              sx={{
                borderLeft: '0.5vw solid #41D9E1',
                paddingX: 1,
              }}>
              Ask iHub
            </Typography>
            <Button variant="contained"
              onClick={handleAddNewChat}
              sx={{
                color: 'var(--text-dark)',
                width: 'max-content',
                height: 'max-content',
                paddingX: 2,
                paddingY: 1,
                background: 'var(--text-light)',
                border: '0.125vw solid #00A9B1',
                boxShadow: '0px 0px 0.5vw 0px #12AEB5DB inset',
                borderRadius: 10,
                display: 'grid',
                placeContent: 'center',
                gridTemplateColumns: 'auto 1fr',
                columnGap: 0.5,
              }}>
              <AddIcon fontSize='small' />
              <Typography variant='body1' fontWeight={700}>
                New Chat
              </Typography>
            </Button>
          </Box>
          <Box sx={{ paddingX: 4 }}>
            <ThreadsHistory history={threadHistory} activeThreadId={threadId}></ThreadsHistory>
            <SavedForLater bookmarks={bookmarks}></SavedForLater>
          </Box>
        </Grid2>

        <Grid2 size={{ xs: 6 }} sx={{
          position: 'fixed',
          zIndex: 1,
          top: 'max(64px, 6vh)',
          left: '32.5vw',
          flex: 1,
          p: 4,
          background: 'white',
          // width: { xs: '100%', lg: '50%', xl: '50%' },
          height: 'calc(80vh)',
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr 0.25fr',
          gap: 2.5,
          boxShadow: '1vw 1.25vw 0px 1vw #03F0FF14',
          // filter: 'drop-shadow(35px 35px 0px #03F0FF14)',
          // '&::after': {
          //   content: '" "',
          //   position:'absolute',
          //   zIndex: -1,
          //   top: 0,
          //   left: 0,
          //   width: '105%',
          //   height: '105%',
          //   background:'#03F0FF14'
          // }
        }}>
          <Grid2
            ref={threadContainerRef}
            size={{ xs: 12 }}
            sx={{
              width: '100%',
              overflow: 'scroll',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              rowGap: 2,
              paddingX: 1,
              '::-webkit-scrollbar': {
                width: '0.5vw',
              },
              '::-webkit-scrollbar-track': {
                background: '#E1E1E1E0',
                borderRadius: '0.25vw',
              },
              '::-webkit-scrollbar-thumb': {
                background: '#3ED4DC',
                borderRadius: '0.25vw',
                transition: 'background 200ms ease-in-out'
              },
              '::-webkit-scrollbar-thumb:hover': {
                background: '#2d979d'
              }
            }}>
            {thread && thread.entries.length > 0 && thread.entries.map((entry: any, index: number) => {
              const isRequestCategory = entry.category === 'request';
              return (
                <Box key={entry.id ?? index}
                  sx={{
                    minWidth: '400px',
                    maxWidth: '85%',
                    position: 'relative',
                    boxSizing: 'border-box',
                    alignSelf: isRequestCategory ? 'flex-end' : '',
                    borderRadius: 2,
                  }}>
                  {entry.category === 'request' && <BaloonShaped type={entry.category}>
                    <UserQuery
                      query={entry.data.content.text!}
                      created_at={entry.created_at}
                      sources={entry.data.content.sources ?? {}}></UserQuery>
                  </BaloonShaped>}
                  {entry.category === 'response' && <BaloonShaped type={entry.category}>
                    <AssistantResponse
                      isStreaming={entry.isStreaming}
                      onTextStreamingFn={handleOnTextStreaming}
                      thread_id={entry.thread_id}
                      entry_id={entry.entry_id}
                      sources={entry.data.content.sources}
                      text={entry.data.content.text!}
                      time={entry.created_at}
                      plot={entry.data.content.visual}></AssistantResponse>
                  </BaloonShaped>
                  }
                </Box>
              )
            })}
            {!loadingResponse && errorLoadingReponse !== '' && <ErrorState
              errorMessage={errorLoadingReponse as string} />
            }
            {(threadsHistoryLoaded === false) && <DarkAdnocLoadingProgress size={'xlarge'}></DarkAdnocLoadingProgress>}
            {loadingResponse && <AIRepsonseLoadingProgress></AIRepsonseLoadingProgress>}
            <Box id={"just-for-focus-ref"}></Box>
          </Grid2>
          <Grid2
            container
            size={{ xs: 12 }}
            sx={{
              boxSizing: 'border-box',
              flexDirection: 'column',
              gap: 2,
            }}>
            <PromptInput placeholder={""}
              disable={loadingResponse || isTextStreaming}
              onPromptSubmit={onPromptSubmit}
              suggestions={EXAMPLE_PROMP_SUGGESTIONS}></PromptInput>
            <Box sx={{ display: 'inline-flex', columnGap: 1, }}>
              <DataSources
                disable={loadingResponse || isTextStreaming} inline={true}></DataSources>
            </Box>
          </Grid2>
        </Grid2>
      </Grid2>
    </>
  )
}

export default AskGuardian